import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useQueryParam, NumberParam, StringParam } from "use-query-params";
import {
  FilterBar,
  SearchComponent,
  Table,
  TableSkeleton,
  TopProgress,
} from "../../../components";
import {
  Container,
  WrapperRow,
  Title,
  BodyText,
  GreenDot,
  Greentext,
  StateWrapper,
} from "../../cloud/Instances/styles";
import { utilsService } from "../../../api";
import { usePageTitle } from "../../../context/navigationContext";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { handleStatusName } from "../../../utils/handle-status-name";
import {
  EmptyValue,
  Label,
  Recipients,
  SecondaryValue,
  Stack,
  UserInfo,
  UserLink,
  UserMeta,
  UserName,
  Value,
  LinkValue,
} from "./styles";

const DEFAULT_ORDERING = "-created_at";

type EmailMessageLogItem = {
  id: number;
  created_at: string;
  subject: string | null;
  to: string | null;
  cc: string | null;
  recipients?: string | null;
  status?: string | null;
  error_message?: string | null;
  client?: {
    id?: number;
    name?: string | null;
  } | null;
  client_name?: string | null;
  user?: {
    id?: number;
    full_name?: string | null;
    email?: string | null;
  } | null;
  user_name?: string | null;
  user_email?: string | null;
};

const EmailMessageLog: FC = () => {
  const [t] = useTranslation();
  usePageTitle(t("PAGE_TITLE_EMAIL_LOG", { defaultValue: t("EMAIL_MESSAGE_LOG") }));
  const [page] = useQueryParam("page", NumberParam);
  const [ordering, setOrdering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);
  const [search] = useQueryParam("search", StringParam);
  const [filtering] = useQueryParam("filtering", StringParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [paginationData, setPaginationData] = useState<any>();
  const [logs, setLogs] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<any>();

  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const splitEmails = useCallback((value?: string | null) => {
    if (!value) {
      return [];
    }
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }, []);

  const renderStatus = useCallback(
    (status?: string | null) => {
      if (!status) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <StateWrapper>
          <GreenDot active={status} />
          <Greentext style={{ textTransform: "capitalize" }} active={status}>
            {handleStatusName(status, t)}
          </Greentext>
        </StateWrapper>
      );
    },
    [t]
  );

  const renderUser = useCallback(
    (log: EmailMessageLogItem) => {
      const user = log.user;
      const name = user?.full_name || log.user_name || user?.email;
      const userId = user?.id;
      const email = user?.email || log.user_email;

      if (!name && !email && !userId) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      const meta: string[] = [];
      if (email) {
        meta.push(email);
      }
      if (userId) {
        meta.push(`#${userId}`);
      }

      return (
        <UserInfo>
          {name ? (
            userId ? (
              <UserLink to={`/clients-users/users/${userId}/overview`} title={name}>
                {name}
              </UserLink>
            ) : (
              <UserName title={name}>{name}</UserName>
            )
          ) : null}
          {meta.length > 0 ? (
            <UserMeta>
              {meta.map((value, index) => (
                <span key={`${value}-${index}`}>{value}</span>
              ))}
            </UserMeta>
          ) : null}
        </UserInfo>
      );
    },
    [t]
  );

  const renderRecipients = useCallback(
    (log: EmailMessageLogItem) => {
      const directList = splitEmails(log.to);
      const fallbackList =
        directList.length > 0 ? directList : splitEmails(log.recipients);
      const toList = fallbackList;
      const ccList = splitEmails(log.cc);

      if (toList.length === 0 && ccList.length === 0) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Recipients>
          {toList.length > 0 ? (
            <Stack>
              <Label>{t("TO")}</Label>
              <Value>{toList.join(", ")}</Value>
            </Stack>
          ) : null}
          {ccList.length > 0 ? (
            <Stack>
              <Label>{t("CC")}</Label>
              <Value>{ccList.join(", ")}</Value>
            </Stack>
          ) : null}
        </Recipients>
      );
    },
    [splitEmails, t]
  );

  const renderClient = useCallback(
    (log: EmailMessageLogItem) => {
      const client = log.client;
      const name = client?.name || log.client_name;
      const id = client?.id;

      if (!name && !id) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      const link = id ? `/clients-users/clients/${id}/details` : undefined;

      return (
        <Stack>
          {name ? (
            link ? (
              <LinkValue to={link} title={name}>
                {name}
              </LinkValue>
            ) : (
              <Value title={name}>{name}</Value>
            )
          ) : null}
          {id ? <SecondaryValue>#{id}</SecondaryValue> : null}
        </Stack>
      );
    },
    [t]
  );

  const renderError = useCallback(
    (log: EmailMessageLogItem) => {
      if (!log.error_message) {
        return <EmptyValue>—</EmptyValue>;
      }

      return (
        <Stack>
          <Label>{t("ERROR")}</Label>
          <Value>{log.error_message}</Value>
        </Stack>
      );
    },
    [t]
  );

  const toTableRow = useCallback(
    (log: EmailMessageLogItem) => ({
      ...log,
      navigate: `/utilities/email-message-log/${log.id}`,
      created_at: log.created_at
        ? timeConverterMMDDYYHHMM(log.created_at)
        : <EmptyValue>—</EmptyValue>,
      subject: log.subject ? (
        <LinkValue to={`/utilities/email-message-log/${log.id}`} title={log.subject}>
          {log.subject}
        </LinkValue>
      ) : (
        <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
      ),
      to: renderRecipients(log),
      client: renderClient(log),
      user: renderUser(log),
      status: renderStatus(log.status),
      error_message: renderError(log),
    }),
    [renderClient, renderError, renderRecipients, renderStatus, renderUser, t]
  );

  const fetchLogs = useCallback(() => {
    const currentPage = page || 1;
    const currentPageSize = pageSize || 10;
    const currentOrdering = ordering || DEFAULT_ORDERING;

    setPageLoading(true);
    utilsService
      .getEmailMessageLog({
        page: currentPage,
        pageSize: currentPageSize,
        ordering: currentOrdering,
        search: search || undefined,
        filtering: filtering || undefined,
      })
      .then((res) => {
        const items: EmailMessageLogItem[] = res.data?.objects || [];
        setLogs(items.map((item) => toTableRow(item)));
        setPaginationData(res.data);
        setFilterOptions(res.data?.filter_options);
      })
      .catch((error) => {
        console.error("Failed to fetch email message log", error);
        setLogs([]);
        setPaginationData(undefined);
        setFilterOptions(undefined);
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [filtering, ordering, page, pageSize, search, toTableRow]);

  useEffect(() => {
    if (!ordering) {
      return;
    }
    fetchLogs();
  }, [fetchLogs, ordering]);

  const normalizedFilterOptions = useMemo(
    () => ({
      created_at: {
        options: filterOptions?.created_at || [],
        type: "date" as const,
      },
      client: {
        options: filterOptions?.client || [],
      },
      user: {
        options: filterOptions?.user || [],
      },
    }),
    [filterOptions]
  );

  const columns = useMemo(
    () => [
      {
        title: "ID",
        key: "id",
      },
      {
        title: t("CREATED_AT"),
        key: "created_at",
        sorted: true,
      },
      {
        title: t("SUBJECT"),
        key: "subject",
        sorted: true,
      },
      {
        title: t("RECIPIENTS"),
        key: "to",
        sorted: true,
      },
      {
        title: t("CLIENT"),
        key: "client",
        sorted: true,
      },
      {
        title: t("USER"),
        key: "user",
        sorted: true,
      },
      {
        title: t("STATUS"),
        key: "status",
      },
      {
        title: t("ERROR"),
        key: "error_message",
      },
    ],
    [t]
  );

  const hasData = logs.length > 0;

  return (
    <Container>
      {pageLoading ? <TopProgress loading={pageLoading} /> : null}
      <WrapperRow>
        <Title>{t("EMAIL_MESSAGE_LOG")}</Title>
      </WrapperRow>
      <WrapperRow style={{ marginTop: "12px" }}>
        <SearchComponent />
      </WrapperRow>
      {filterOptions ? (
        <WrapperRow style={{ marginTop: "12px" }}>
          <FilterBar filterOptions={normalizedFilterOptions} />
        </WrapperRow>
      ) : null}
      {pageLoading ? (
        <WrapperRow style={{ flexDirection: "column", marginTop: "20px" }}>
          <TableSkeleton rows={10} columns={columns.length} columnsData={columns} />
        </WrapperRow>
      ) : hasData ? (
        <WrapperRow style={{ flexDirection: "column", marginTop: "20px" }}>
          <Table
            columns={columns}
            rows={logs}
            hideCheckbox
            paginationData={paginationData}
            updateData={fetchLogs}
          />
        </WrapperRow>
      ) : (
        <WrapperRow style={{ marginTop: "20px" }}>
          <BodyText>{t("NO_EMAIL_MESSAGE_LOGS")}</BodyText>
        </WrapperRow>
      )}
    </Container>
  );
};

export default EmailMessageLog;
